/**
 * Embedding Types
 *
 * Types for micro-frontend and iframe embedding system
 */

import type { ReactNode } from 'react';

/**
 * Service embedding types
 */
export type EmbeddingType = 'iframe' | 'microfrontend' | 'webcomponent';

/**
 * Configuration for an embedded service
 */
export interface EmbeddedService {
  /** Unique service identifier */
  id: string;
  /** Display name */
  name: string;
  /** Embedding type */
  type: EmbeddingType;
  /** Source URL */
  url: string;
  
  // For iframes
  /** Sandbox permissions */
  sandboxOptions?: string[];
  /** Feature policy directives */
  allowOptions?: string[];
  
  // For micro-frontends
  /** Federation scope name */
  scope?: string;
  /** Module path to load */
  module?: string;
  
  // Common
  /** Whether to pass authentication context */
  passAuth?: boolean;
  /** Whether to pass consent state */
  passConsent?: boolean;
  /** Any-of permission list required for the service to be visible */
  permissions?: string[];
  /** Style configuration */
  styles?: {
    width?: string;
    height?: string;
    minHeight?: string;
  };
}

/**
 * Message types for postMessage communication
 */
export type EmbeddingMessageType =
  | 'auth'
  | 'auth:expired'
  | 'consent'
  | 'theme'
  | 'language'
  | 'navigation'
  | 'custom'
  | 'ready'
  | 'resize'
  | 'error';

/**
 * Structure of messages exchanged with embedded services
 */
export interface EmbeddingMessage<T = unknown> {
  type: EmbeddingMessageType;
  payload: T;
  source: string;
  timestamp: number;
}

export interface EmbeddedNavigationPayload {
  appId?: string;
  path?: string;
  shellPath?: string;
  search?: string;
  hash?: string;
  replace?: boolean;
}

/**
 * Configuration containing all embedded services
 */
export interface EmbeddingConfig {
  services: EmbeddedService[];
}

/**
 * Props for the IframeWrapper component
 */
export interface IframeWrapperProps {
  service: EmbeddedService;
  className?: string;
  onLoad?: () => void;
  onError?: (error: Error) => void;
}

/**
 * Props for the MicroFrontendLoader component
 */
export interface MicroFrontendLoaderProps {
  service: EmbeddedService;
  className?: string;
  onLoad?: () => void;
  onError?: (error: Error) => void;
  fallback?: ReactNode;
}

/**
 * Props for the WebComponentLoader component
 */
export interface WebComponentLoaderProps {
  service: EmbeddedService;
  className?: string;
  onLoad?: () => void;
  onError?: (error: Error) => void;
}

/**
 * Context data passed to embedded services
 */
export interface EmbeddingContextData {
  auth?: {
    token?: string;
    entitlementsRefreshed?: boolean;
    user?: {
      id: string;
      name: string;
      email: string;
      roles: string[];
      permissions?: string[];
    };
  };
  consent?: {
    necessary: boolean;
    analytics: boolean;
    marketing: boolean;
    preferences: boolean;
  };
  theme?: 'light' | 'dark';
  language?: string;
}

/**
 * Default iframe sandbox permissions
 * Provides a secure baseline for embedded iframes
 */
export const DEFAULT_IFRAME_SANDBOX = [
  'allow-scripts',
  'allow-same-origin',
  'allow-forms',
  'allow-downloads',
  'allow-popups',
  'allow-popups-to-escape-sandbox',
];

/**
 * Service health status for embedded services
 */
export enum ServiceHealthStatus {
  UNKNOWN = 'unknown',
  HEALTHY = 'healthy',
  DEGRADED = 'degraded',
  UNHEALTHY = 'unhealthy',
}
